const DEMO_EMAIL = 'demo@demo.demo';
const DEMO_DURATION_MS = 30 * 60 * 1000;
const DEMO_MESSAGES_KEY = 'timeLockDemoMessages';
const DEMO_EVENTS_KEY = 'timeLockDemoEvents';
const DEMO_STATIONS_KEY = 'timeLockDemoStations';
const DEMO_STATION_MESSAGES_KEY = 'timeLockDemoStationMessages';

function readJson(key, fallback) {
  try {
    const rawValue = localStorage.getItem(key);
    return rawValue ? JSON.parse(rawValue) : fallback;
  } catch (error) {
    console.error(`Failed to parse ${key}:`, error);
    return fallback;
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

export function isDemoUser(user) {
  return Boolean(user?.isDemo || user?.email === DEMO_EMAIL || user?.plan === 'demo');
}

export function normalizeDemoUser(user) {
  if (!user || !isDemoUser(user)) {
    return user;
  }

  const demoExpiresAt = user.demoExpiresAt || Date.now() + DEMO_DURATION_MS;
  const demoStartTime = user.demoStartTime || demoExpiresAt - DEMO_DURATION_MS;

  return {
    ...user,
    isDemo: true,
    plan: 'demo',
    demoStartTime,
    demoExpiresAt,
  };
}

export function clearDemoSessionData() {
  localStorage.removeItem(DEMO_MESSAGES_KEY);
  localStorage.removeItem(DEMO_EVENTS_KEY);
  localStorage.removeItem(DEMO_STATIONS_KEY);
  localStorage.removeItem(DEMO_STATION_MESSAGES_KEY);
}

export function isDemoSessionExpired(user) {
  if (!isDemoUser(user)) {
    return false;
  }

  const normalizedUser = normalizeDemoUser(user);
  return normalizedUser?.demoExpiresAt <= Date.now();
}

function ensureActiveDemoSession(user) {
  if (!isDemoUser(user)) {
    return false;
  }

  if (isDemoSessionExpired(user)) {
    clearDemoSessionData();
    return false;
  }

  const normalizedUser = normalizeDemoUser(user);
  localStorage.setItem('timeLockUser', JSON.stringify(normalizedUser));
  return true;
}

function readDemoCollection(key, user) {
  if (!ensureActiveDemoSession(user)) {
    return [];
  }

  const items = readJson(key, []);
  return Array.isArray(items) ? items : [];
}

function writeDemoCollection(key, items, user) {
  if (!ensureActiveDemoSession(user)) {
    return [];
  }

  writeJson(key, items);
  return items;
}

export function getDemoMessages(user) {
  return readDemoCollection(DEMO_MESSAGES_KEY, user).sort((left, right) => {
    return new Date(right.created_at || 0) - new Date(left.created_at || 0);
  });
}

export function createDemoMessage(user, payload) {
  const messages = getDemoMessages(user);
  const linkedEvent = payload.event_id
    ? getDemoEvents(user).find((event) => event.id === payload.event_id)
    : null;
  const createdAt = new Date().toISOString();
  const id = `demo-msg-${Date.now()}`;
  const token = `demo-token-${Date.now()}`;
  const nextMessage = {
    id,
    token,
    recipient_name: payload.recipient_name || '',
    message_text: payload.message_text || '',
    message_image: payload.message_image || null,
    message_type: payload.message_image ? 'image' : 'text',
    release_date: payload.release_date || null,
    event_id: linkedEvent?.id || null,
    event_name: linkedEvent?.name || null,
    created_at: createdAt,
    isDemo: true,
  };

  writeDemoCollection(DEMO_MESSAGES_KEY, [nextMessage, ...messages], user);
  return nextMessage;
}

export function updateDemoMessage(user, messageId, updates) {
  const linkedEvent = updates.event_id
    ? getDemoEvents(user).find((event) => event.id === updates.event_id)
    : null;
  const messages = getDemoMessages(user).map((message) => {
    if (message.id !== messageId) {
      return message;
    }

    return {
      ...message,
      ...updates,
      event_id: linkedEvent?.id || null,
      event_name: linkedEvent?.name || null,
      updated_at: new Date().toISOString(),
    };
  });

  writeDemoCollection(DEMO_MESSAGES_KEY, messages, user);
  return messages.find((message) => message.id === messageId) || null;
}

export function deleteDemoMessage(user, messageId) {
  const messages = getDemoMessages(user).filter((message) => message.id !== messageId);
  writeDemoCollection(DEMO_MESSAGES_KEY, messages, user);
  return messages;
}

export function getDemoMessageByToken(token) {
  const storedUser = readJson('timeLockUser', null);
  if (!ensureActiveDemoSession(storedUser)) {
    return null;
  }

  return readJson(DEMO_MESSAGES_KEY, []).find((message) => message.token === token) || null;
}

export function getDemoEvents(user) {
  return readDemoCollection(DEMO_EVENTS_KEY, user)
    .map((event) => ({
      ...event,
      station_count: getDemoStations(user, event.id).length,
    }))
    .sort((left, right) => {
      return new Date(right.created_at || 0) - new Date(left.created_at || 0);
    });
}

export function createDemoEvent(user, payload) {
  const events = getDemoEvents(user);
  const createdAt = new Date().toISOString();
  const id = `demo-event-${Date.now()}`;
  const event_qr_token = `demo-event-token-${Date.now()}`;
  const nextEvent = {
    id,
    name: payload.name || '',
    description: payload.description || '',
    recipient_name: payload.recipient_name || '',
    recipient_id: payload.recipient_id || '',
    release_date: payload.release_date || '',
    release_time: payload.release_time || '',
    event_qr_token,
    station_count: 0,
    created_at: createdAt,
    updated_at: createdAt,
    isDemo: true,
  };

  writeDemoCollection(DEMO_EVENTS_KEY, [nextEvent, ...events], user);
  return nextEvent;
}

export function updateDemoEvent(user, eventId, updates) {
  const events = getDemoEvents(user).map((event) => {
    if (event.id !== eventId) {
      return event;
    }

    return {
      ...event,
      ...updates,
      updated_at: new Date().toISOString(),
    };
  });

  writeDemoCollection(DEMO_EVENTS_KEY, events, user);
  return events.find((event) => event.id === eventId) || null;
}

export function deleteDemoEvent(user, eventId) {
  const events = getDemoEvents(user).filter((event) => event.id !== eventId);
  writeDemoCollection(DEMO_EVENTS_KEY, events, user);
  return events;
}

export function getDemoEventByToken(token) {
  const storedUser = readJson('timeLockUser', null);
  if (!ensureActiveDemoSession(storedUser)) {
    return null;
  }

  const event = readJson(DEMO_EVENTS_KEY, []).find((entry) => entry.event_qr_token === token) || null;
  if (!event) {
    return null;
  }

  return {
    ...event,
    messages: getDemoMessages(storedUser)
      .filter((message) => message.event_id === event.id)
      .map((message, index) => {
        const releaseDate = message.release_date ? new Date(message.release_date) : null;
        const isReleased = !releaseDate || releaseDate <= new Date();

        return {
          ...message,
          order_num: index + 1,
          is_released: isReleased,
          message_text: isReleased ? message.message_text : null,
          message_image: isReleased ? message.message_image : null,
          message_video: isReleased ? message.message_video : null,
          countdown: !isReleased && releaseDate
            ? {
                until: releaseDate.toISOString(),
                days: Math.ceil((releaseDate - new Date()) / (1000 * 60 * 60 * 24)),
              }
            : null,
        };
      }),
  };
}

export function getDemoEventById(user, eventId) {
  return getDemoEvents(user).find((event) => event.id === eventId) || null;
}

export function getDemoStations(user, eventId) {
  return readDemoCollection(DEMO_STATIONS_KEY, user)
    .filter((station) => station.event_id === eventId)
    .sort((left, right) => (left.order_num || 0) - (right.order_num || 0));
}

export function createDemoStation(user, payload) {
  const stations = readDemoCollection(DEMO_STATIONS_KEY, user);
  const eventStations = stations.filter((station) => station.event_id === payload.eventId);
  const createdAt = new Date().toISOString();
  const nextStation = {
    id: `demo-station-${Date.now()}`,
    event_id: payload.eventId,
    order_num: eventStations.length + 1,
    title: payload.title || '',
    description: payload.description || '',
    instructions: payload.instructions || '',
    initial_message: payload.initialMessage || '',
    scheduled_date: payload.scheduledDate || '',
    scheduled_time: payload.scheduledTime || '',
    location: payload.location || '',
    message_count: 0,
    created_at: createdAt,
    isDemo: true,
  };

  writeDemoCollection(DEMO_STATIONS_KEY, [...stations, nextStation], user);
  return nextStation;
}

export function deleteDemoStation(user, stationId) {
  const stations = readDemoCollection(DEMO_STATIONS_KEY, user).filter((station) => station.id !== stationId);
  writeDemoCollection(DEMO_STATIONS_KEY, stations, user);

  const stationMessages = readDemoCollection(DEMO_STATION_MESSAGES_KEY, user)
    .filter((entry) => entry.station_id !== stationId);
  writeDemoCollection(DEMO_STATION_MESSAGES_KEY, stationMessages, user);

  return stations;
}

export function getDemoStationMessages(user, stationId) {
  const mappings = readDemoCollection(DEMO_STATION_MESSAGES_KEY, user)
    .filter((entry) => entry.station_id === stationId);
  const messages = getDemoMessages(user);

  return mappings
    .map((mapping) => messages.find((message) => message.id === mapping.message_id))
    .filter(Boolean);
}

export function addDemoMessageToStation(user, stationId, messageId) {
  const mappings = readDemoCollection(DEMO_STATION_MESSAGES_KEY, user);
  if (mappings.some((entry) => entry.station_id === stationId && entry.message_id === messageId)) {
    return mappings;
  }

  const nextMappings = [...mappings, { id: `demo-stmsg-${Date.now()}`, station_id: stationId, message_id: messageId }];
  writeDemoCollection(DEMO_STATION_MESSAGES_KEY, nextMappings, user);

  const stations = readDemoCollection(DEMO_STATIONS_KEY, user).map((station) =>
    station.id === stationId ? { ...station, message_count: (station.message_count || 0) + 1 } : station
  );
  writeDemoCollection(DEMO_STATIONS_KEY, stations, user);

  return nextMappings;
}
